import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import PageHero from "../components/PageHero";
import Eyebrow from "../components/shared/Eyebrow";
import FinalCTA from "../components/sections/FinalCTA";
import { blogService } from "../services/blogService";
import { useRemoteData } from "../hooks/useRemoteData";
import AsyncSection, { SkeletonCard } from "../components/shared/AsyncSection";
import { cn } from "../lib/utils";

export default function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  const { data: posts, loading } = useRemoteData(() => blogService.list(), [], {
    initialValue: [],
  });

  const isFiltering = cat !== "All" || q.trim() !== "";

  /**
   * The newest featured article gets the hero slot — but only when the reader
   * is browsing everything.
   *
   * Once a category or search is applied the hero is dropped and every match
   * appears in the grid instead. Otherwise the hero would sit there showing an
   * article that does not match the filter, and the promoted article would be
   * missing from its own category's results.
   */
  const featured = useMemo(
    () => (isFiltering ? null : (posts ?? []).find((p) => p.featured) ?? null),
    [posts, isFiltering]
  );

  const rest = useMemo(
    () => (posts ?? []).filter((p) => p !== featured),
    [posts, featured]
  );

  // Categories come from the posts themselves, so a category an editor stops
  // using disappears from the filter instead of showing an empty result.
  const categories = useMemo(() => {
    const found = new Set((posts ?? []).map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(found).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rest.filter((p) => {
      const okCat = cat === "All" || p.category === cat;
      const okQ =
        !query ||
        (p.title || "").toLowerCase().includes(query) ||
        (p.excerpt || "").toLowerCase().includes(query);
      return okCat && okQ;
    });
  }, [q, cat, rest]);

  return (
    <>
      <PageHero
        eyebrow="Journal"
        headline={"Notes on cleaning,\nfrom the pros."}
        intro="Guides, tips and real-project write-ups from our team."
        image="/generated/service_post_reno.png"
        imageAlt="Emilio's Solutions team cleaning a renovated Singapore kitchen"
        compact
      />

      {featured && (
        <section className="bg-ink py-16 md:py-24 border-t border-charcoal/50">
          <div className="container-emilio">
            <Eyebrow>Featured</Eyebrow>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 group border border-charcoal hover:border-emerald/60 transition-colors"
            >
              <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden">
                <img
                  src={featured.featuredImage}
                  alt={featured.imageAltText}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="eager"
                />
              </div>
              <div className="lg:col-span-5 flex flex-col justify-center p-6 md:p-10">
                <div className="text-emerald text-xs uppercase tracking-[0.18em]">
                  {featured.category} · {featured.readingTime} min read
                </div>
                <h3 className="mt-4 font-display font-extrabold text-white text-3xl md:text-4xl leading-tight">
                  {featured.title}
                </h3>
                <p className="mt-4 text-mutedtext text-sm md:text-base">
                  {featured.excerpt}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-emerald font-semibold text-sm">
                  Read Article <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section
        className="bg-ink py-16 md:py-20 border-t border-charcoal/50"
        data-testid="blog-list"
      >
        <div className="container-emilio">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-10">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn(
                    "text-xs uppercase tracking-[0.18em] px-3 py-2 border transition-colors",
                    cat === c
                      ? "border-emerald text-emerald"
                      : "border-charcoal text-mutedtext hover:text-white hover:border-white/40"
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
            {/*
              A visible total. Without it the hero article reads as missing
              from the grid, and there is no way to tell how many articles
              exist in the first place.
            */}
            <span className="text-xs text-mutedtext whitespace-nowrap" data-testid="blog-count">
              {filtered.length + (featured ? 1 : 0)}{" "}
              {filtered.length + (featured ? 1 : 0) === 1 ? "article" : "articles"}
              {isFiltering ? " found" : ""}
            </span>

            <div className="lg:ml-auto flex items-center gap-2 border border-charcoal focus-within:border-emerald px-3 py-2 min-w-[240px]">
              <Search className="w-4 h-4 text-mutedtext" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles"
                aria-label="Search articles"
                data-testid="blog-search"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-mutedtext"
              />
            </div>
          </div>

          <AsyncSection
            loading={loading}
            isEmpty={filtered.length === 0}
            skeleton={
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[0, 1, 2].map((index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            }
            emptyTitle={q || cat !== "All" ? "No articles match your search" : "No articles yet"}
            emptyMessage={
              q || cat !== "All"
                ? "Try a different category or search term."
                : "New cleaning guides are on the way."
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group border border-charcoal hover:border-emerald/60 transition-colors"
                  data-testid={`blog-post-${p.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.featuredImage}
                      alt={p.imageAltText}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    {/*
                      More than one article can be marked featured. Only the
                      newest takes the hero slot, so the others are badged here
                      — otherwise the flag would appear to do nothing.
                    */}
                    {p.featured && (
                      <span className="absolute top-3 left-3 bg-emerald text-ink text-[10px] font-bold uppercase tracking-[0.16em] px-2 py-1">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-emerald uppercase tracking-[0.18em]">
                      {p.category} · {p.readingTime} min read
                    </div>
                    <h3 className="mt-3 font-display font-bold text-white text-lg leading-tight group-hover:text-emerald transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-mutedtext text-sm">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </AsyncSection>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
